import { Switch, useRouteMatch } from 'react-router-dom';
import { PageNotFound } from 'features/main/components/CustomErrorPages';
import MultifactorAuthentication from 'features/multifactor-authentication/MultifactorAuthentication';
import { SSO } from 'features/sso';
import { ApmRoute } from '@elastic/apm-rum-react';
import { LayoutMain, LayoutWrapper } from 'features/common/components/Layouts';
import { useFeatures } from 'lib/featureFlags/features';
import AdminSideNav from './AdminSideNav';
import MembersContainerWithErrorHandling from 'features/members/components/MembersContainer';
import {
  auditLogsPath,
  contextIdsPath,
  customHostnamesPath,
  invitesPath,
  membersPath,
  mfaPath,
  ssoPath,
  supportAccessPath,
  usagePath,
  userGroupListPath,
} from './admin-paths';
import InvitesContainer from 'features/members/components/InvitesContainer';
import { useMediaQuery } from '@chakra-ui/react';
import sitecoreTheme from '@sitecore/blok-theme';
import SupportAccess from 'features/supportAccess/SupportAccess';
import ContextIdsContainer from 'features/context-ids/ContextIdsContainer';
import { CustomHostnamesContainer } from 'features/custom-hostnames';
import { AuditLogsWithErrorBoundary } from 'features/audit-logs';
import Usage from 'features/usage/Usage';
import { useAppAccess } from 'features/common/components/OrganizationAppAccess/utils';
import GroupListContainer from 'features/user-group/user-group-list/components/GroupListContainer';

const AdminContainer = () => {
  const { path } = useRouteMatch() || {};
  const features = useFeatures();
  const { hasOrgOwnerAccess } = useAppAccess();

  const [isBiggerThanMd] = useMediaQuery(
    `(min-width: ${sitecoreTheme.breakpoints.md})`,
  );

  return (
    <LayoutWrapper flexDirection={'row'}>
      {isBiggerThanMd && <AdminSideNav />}
      <LayoutMain asMain={false} layerStyle='section.main'>
        <Switch>
          <ApmRoute
            path={`${path}${userGroupListPath}`}
            render={() =>
              features.USER_GROUPS ? <GroupListContainer /> : <PageNotFound />
            }
          ></ApmRoute>
          <ApmRoute path={`${path}${membersPath}`}>
            <MembersContainerWithErrorHandling />
          </ApmRoute>
          <ApmRoute path={`${path}${invitesPath}`}>
            <InvitesContainer />
          </ApmRoute>
          <ApmRoute
            path={`${path}${mfaPath}`}
            render={() =>
              features.MFA ? <MultifactorAuthentication /> : <PageNotFound />
            }
          />
          <ApmRoute
            path={`${path}${ssoPath}`}
            render={() => (features.SSO ? <SSO /> : <PageNotFound />)}
          />
          <ApmRoute
            path={`${path}${supportAccessPath}`}
            render={() =>
              features.SUPPORT_ACCESS ? <SupportAccess /> : <PageNotFound />
            }
          />
          <ApmRoute
            path={`${path}${contextIdsPath}`}
            render={() =>
              features.CONTEXT_MAPPING ? (
                <ContextIdsContainer />
              ) : (
                <PageNotFound />
              )
            }
          />
          <ApmRoute
            path={`${path}${customHostnamesPath}`}
            render={() =>
              features.CUSTOM_HOSTNAMES ? (
                <CustomHostnamesContainer />
              ) : (
                <PageNotFound />
              )
            }
          />
          <ApmRoute
            exact
            path={`${path}${auditLogsPath}`}
            render={() =>
              features.AUDIT_LOGS ? (
                <AuditLogsWithErrorBoundary />
              ) : (
                <PageNotFound />
              )
            }
          />
          <ApmRoute
            exact
            path={`${path}${usagePath}`}
            render={() =>
              features.USAGE_TRACKING && hasOrgOwnerAccess ? (
                <Usage />
              ) : (
                <PageNotFound />
              )
            }
          />
          <ApmRoute component={PageNotFound} />
        </Switch>
      </LayoutMain>
    </LayoutWrapper>
  );
};

export default AdminContainer;
